import { ActionIcon, Loader, Table, Text, VisuallyHidden } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import {
  getListConnectionsQueryKey,
  useListConnections,
  useRevokeConnection,
} from "../../api/generated/endpoints/identity/identity";
import type { Connection } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useMcpOAuth } from "../../platform/features";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { formatDate } from "../../ui/format";
import { TableFrame } from "../../ui/TableFrame";
import { TitledHeader } from "../../ui/TitledHeader";

/**
 * The apps the user connected to this team by signing in, Claude.ai's or ChatGPT's (spec
 * platform/mcp-oauth, PR 4), each with Revoke. The API lists every team's; the tab shows the
 * URL's, as it does the tokens.
 *
 * While that sign-in is off, in its dark rollout or turned off again, the section shows only once
 * the team has a connection: a connection made before keeps working until its access token's
 * hour ends, so it stays listed and revocable (#1697's review).
 */
export function ConnectionsSection({ teamId }: { teamId: string }) {
  const oauth = useMcpOAuth();
  const id = teamId.toLowerCase();
  const connections = useListConnections({
    query: {
      select: (all) => all.filter((each) => each.team_id.toLowerCase() === id),
      // An app connects in another tab or window, by its own sign-in: coming back here reads the
      // list again, however fresh it is (#1697's review).
      refetchOnWindowFocus: "always",
    },
  });
  const listed = connections.data?.length ?? 0;
  if (!oauth && listed === 0) return null;
  return (
    <div>
      <TitledHeader title="Apps you connected to this team" />
      {connections.isPending ? (
        <Loader size="sm" aria-label="Loading connected apps" />
      ) : connections.isError ? (
        <ErrorAlert error={connections.error} />
      ) : listed === 0 ? (
        <Text c="var(--cai-color-text-muted)">No app is connected to this team.</Text>
      ) : (
        <ConnectionList connections={connections.data} />
      )}
    </div>
  );
}

function ConnectionList({ connections }: { connections: Connection[] }) {
  const [revoking, setRevoking] = useState<Connection | undefined>(undefined);
  return (
    <>
      <TableFrame>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>App</Table.Th>
            <Table.Th>Access</Table.Th>
            <Table.Th>Connected</Table.Th>
            <Table.Th>Expires</Table.Th>
            <Table.Th>Last used</Table.Th>
            <Table.Th>
              <VisuallyHidden>Actions</VisuallyHidden>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {connections.map((connection) => (
            <Table.Tr key={connection.id}>
              <Table.Td>
                <Text size="sm" fw={600}>
                  {connection.client_name}
                </Text>
                <Text size="xs" c="var(--cai-color-text-muted)">
                  {connection.client_host}
                </Text>
              </Table.Td>
              <Table.Td>{connection.access === "write" ? "Read and write" : "Read only"}</Table.Td>
              <Table.Td>{formatDate(connection.connected_at)}</Table.Td>
              <Table.Td>{formatDate(connection.expires_at)}</Table.Td>
              <Table.Td>
                {connection.last_used_at === null ? "Never" : formatDate(connection.last_used_at)}
              </Table.Td>
              <Table.Td align="right">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={`Revoke ${connection.client_name}`}
                  onClick={() => setRevoking(connection)}
                >
                  <Trash2 size={16} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </TableFrame>
      {revoking === undefined ? null : (
        <RevokeConnection connection={revoking} onClose={() => setRevoking(undefined)} />
      )}
    </>
  );
}

function RevokeConnection(props: { connection: Connection; onClose: () => void }) {
  const { connection, onClose } = props;
  const queryClient = useQueryClient();
  const revoke = useRevokeConnection({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getListConnectionsQueryKey() });
        onClose();
      },
    },
  });
  return (
    <ConfirmModal
      opened
      title={`Revoke ${connection.client_name}?`}
      confirmLabel="Revoke connection"
      onConfirm={() => revoke.mutate({ connectionId: connection.id })}
      onCancel={onClose}
      pending={revoke.isPending}
      error={revoke.isError ? <ErrorAlert error={revoke.error} /> : null}
    >
      <Text size="sm">
        It stops working at once. To use it again, connect it again from the app.
      </Text>
    </ConfirmModal>
  );
}
